import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DashboardLayout } from '@/components/DashboardLayout';
import { getAllBackMarketListings, updateBackMarketListing, saveListingPrices, getDashboardStats } from '@/services/api';
import { toast } from 'sonner';
import { 
  Search, RefreshCw, AlertCircle, Settings2, Loader2, Package,
  Box, TrendingDown, TrendingUp, Filter, ChevronUp, ChevronDown,
  Check, X, Target, DollarSign, LineChart as LineChartIcon, Camera
} from 'lucide-react';
import { motion } from 'framer-motion';
import ListingPriceHistoryModal from '@/components/ListingPriceHistoryModal';

const GRADE_LABELS = {
  'SHINY': 'Comme neuf', 'STALLONE': 'Tres bon', 'GOOD': 'Bon etat',
  'FAIR': 'Correct', 'EXCELLENT': 'Excellent', 'VERY_GOOD': 'Tres bon', 'NO_GRADE': '-'
};

export default function ListingsPage() {
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [filterBackbox, setFilterBackbox] = useState('all');
  const [filterPricing, setFilterPricing] = useState('all');
  const [sortField, setSortField] = useState('title');
  const [sortDir, setSortDir] = useState('asc');
  // Price management modal
  const [editingListing, setEditingListing] = useState(null);
  const [editForm, setEditForm] = useState({
    price: '', quantity: '', min_price: '', target_price: '', backbox_price: ''
  });
  const [updating, setUpdating] = useState(false);
  // Price history modal + snapshot status
  const [historyListing, setHistoryListing] = useState(null);
  const [snapshotStatus, setSnapshotStatus] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const stats = await getDashboardStats();
      setConnected(stats.backmarket_connected);
    } catch { setConnected(false); }
    finally { setCheckingConnection(false); }
  };

  // Fetch latest snapshot status on mount
  useEffect(() => {
    if (!connected) return;
    fetchSnapshotStatus();
  }, [connected]);

  const fetchSnapshotStatus = async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const r = await fetch(`${apiBase}/api/listings/snapshot/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) {
        const json = await r.json();
        setSnapshotStatus(json);
        if (json.status === 'running') {
          // keep polling
          setTimeout(fetchSnapshotStatus, 4000);
        }
      }
    } catch { /* silent */ }
  };

  const triggerSnapshot = async () => {
    setSnapshotting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const r = await fetch(`${apiBase}/api/listings/snapshot/run-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      toast.success('Snapshot lancé en arrière-plan');
      setSnapshotStatus({ status: 'running', started_at: new Date().toISOString() });
      setTimeout(fetchSnapshotStatus, 3000);
    } catch (e) {
      toast.error('Erreur snapshot: ' + e.message);
    } finally {
      setSnapshotting(false);
    }
  };

  const fetchAllListings = async () => {
    if (!connected) return;
    setLoading(true);
    try {
      const data = await getAllBackMarketListings();
      setAllListings(data.results || []);
      toast.success(`${data.count || 0} annonces chargees`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (connected) fetchAllListings();
  }, [connected]);

  const stats = useMemo(() => {
    const total = allListings.length;
    const active = allListings.filter(l => l.publication_state === 2).length;
    const backbox = allListings.filter(l => l.snowden === true).length;
    const notBackbox = allListings.filter(l => l.snowden !== true && l.backbox_price && parseFloat(l.price) > l.backbox_price).length;
    const outOfStock = allListings.filter(l => (l.quantity || 0) === 0).length;
    const withTarget = allListings.filter(l => l.target_price).length;
    return { total, active, backbox, notBackbox, outOfStock, withTarget };
  }, [allListings]);

  const filteredListings = useMemo(() => {
    let result = [...allListings];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        l.title?.toLowerCase().includes(q) || l.sku?.toLowerCase().includes(q) || String(l.listing_id).includes(q)
      );
    }
    if (filterGrade !== 'all') result = result.filter(l => l.grade === filterGrade);
    if (filterStock === 'in') result = result.filter(l => (l.quantity || 0) > 0);
    if (filterStock === 'out') result = result.filter(l => (l.quantity || 0) === 0);
    if (filterBackbox === 'yes') result = result.filter(l => l.snowden === true);
    if (filterBackbox === 'no') result = result.filter(l => l.snowden !== true);
    if (filterPricing === 'above_bb') result = result.filter(l => l.backbox_price && parseFloat(l.price) > l.backbox_price);
    if (filterPricing === 'no_target') result = result.filter(l => !l.target_price);
    if (filterPricing === 'below_min') result = result.filter(l => l.target_price && parseFloat(l.price) < l.target_price);

    result.sort((a, b) => {
      let va, vb;
      if (sortField === 'price') { va = parseFloat(a.price) || 0; vb = parseFloat(b.price) || 0; }
      else if (sortField === 'quantity') { va = a.quantity || 0; vb = b.quantity || 0; }
      else if (sortField === 'sku') { va = a.sku || ''; vb = b.sku || ''; }
      else if (sortField === 'backbox_gap') {
        va = a.backbox_price ? parseFloat(a.price) - a.backbox_price : 9999;
        vb = b.backbox_price ? parseFloat(b.price) - b.backbox_price : 9999;
      }
      else { va = a.title || ''; vb = b.title || ''; }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return result;
  }, [allListings, searchQuery, filterGrade, filterStock, filterBackbox, filterPricing, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-0.5" /> : <ChevronDown className="w-3 h-3 inline ml-0.5" />;
  };

  const openEdit = (listing) => {
    setEditingListing(listing);
    setEditForm({
      price: listing.price?.toString() || '',
      quantity: listing.quantity?.toString() || '',
      min_price: listing.min_price?.toString() || '',
      target_price: listing.target_price?.toString() || '',
      backbox_price: listing.backbox_price?.toString() || ''
    });
  };

  const handleSave = async () => {
    if (!editingListing) return;
    setUpdating(true);
    try {
      const lid = editingListing.listing_id;
      
      // 1. Update BM listing (price, quantity)
      const bmUpdate = {};
      if (editForm.price && editForm.price !== editingListing.price?.toString()) {
        bmUpdate.price = parseFloat(editForm.price);
      }
      if (editForm.quantity !== '' && editForm.quantity !== editingListing.quantity?.toString()) {
        bmUpdate.quantity = parseInt(editForm.quantity);
      }
      if (Object.keys(bmUpdate).length > 0) {
        await updateBackMarketListing(lid, bmUpdate);
      }
      
      // 2. Save local prices (min_price on BM, target_price, backbox_price locally)
      await saveListingPrices(lid, {
        min_price: editForm.min_price ? parseFloat(editForm.min_price) : null,
        target_price: editForm.target_price ? parseFloat(editForm.target_price) : null,
        backbox_price: editForm.backbox_price ? parseFloat(editForm.backbox_price) : null
      });
      
      toast.success(`Annonce ${editingListing.sku} mise a jour`);
      setEditingListing(null);
      
      // Update local state
      setAllListings(prev => prev.map(l => {
        if (l.listing_id === lid) {
          return {
            ...l,
            price: editForm.price || l.price,
            quantity: editForm.quantity !== '' ? parseInt(editForm.quantity) : l.quantity,
            min_price: editForm.min_price || l.min_price,
            target_price: editForm.target_price ? parseFloat(editForm.target_price) : null,
            backbox_price: editForm.backbox_price ? parseFloat(editForm.backbox_price) : null
          };
        }
        return l;
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur mise a jour');
    } finally { setUpdating(false); }
  };

  // BackBox gap indicator
  const BackBoxIndicator = ({ listing }) => {
    const price = parseFloat(listing.price) || 0;
    const bbPrice = listing.backbox_price;
    
    if (listing.snowden) {
      return (
        <div className="flex items-center gap-1">
          <Badge className="bg-blue-100 text-blue-800 border-0 text-xs" data-testid={`bb-status-${listing.listing_id}`}>
            <Check className="w-3 h-3 mr-0.5" /> BackBox
          </Badge>
        </div>
      );
    }
    
    if (!bbPrice) {
      return <span className="text-slate-300 text-xs">Non defini</span>;
    }
    
    const gap = price - bbPrice;
    if (gap <= 0) {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs">
          <Check className="w-3 h-3 mr-0.5" /> Eligible ({bbPrice} EUR)
        </Badge>
      );
    }
    return (
      <div className="text-right">
        <Badge className="bg-red-100 text-red-800 border-0 text-xs" data-testid={`bb-gap-${listing.listing_id}`}>
          <X className="w-3 h-3 mr-0.5" /> +{gap.toFixed(0)} EUR
        </Badge>
        <p className="text-xs text-slate-400 mt-0.5">BB: {bbPrice} EUR</p>
      </div>
    );
  };

  if (checkingConnection) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div></DashboardLayout>;
  }

  if (!connected) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="listings-page">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Annonces</h1>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-amber-900">Back Market non connecte</h3>
                  <p className="text-sm text-amber-700 mt-1">Configurez votre token API dans les parametres.</p>
                  <Button variant="outline" className="mt-4 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => window.location.href = '/settings'} data-testid="go-to-settings-btn">Configurer le token</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="listings-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Annonces</h1>
            <p className="text-slate-600 mt-1">{stats.total} annonces · {stats.active} actives · {stats.backbox} BackBox</p>
            {snapshotStatus && snapshotStatus.status !== 'idle' && (
              <p className="text-xs mt-1 flex items-center gap-2" data-testid="snapshot-status">
                {snapshotStatus.status === 'running' && (
                  <><Loader2 className="w-3 h-3 animate-spin text-blue-500" /><span className="text-blue-600">Snapshot en cours…</span></>
                )}
                {snapshotStatus.status === 'done' && (
                  <span className="text-emerald-700">
                    <Check className="w-3 h-3 inline mr-1" />
                    Dernier snapshot : {snapshotStatus.inserted ?? 0} annonces capturées en {snapshotStatus.elapsed_seconds ?? '?'}s
                    {snapshotStatus.completed_at && (
                      <span className="text-slate-400 ml-1">
                        ({new Date(snapshotStatus.completed_at).toLocaleString('fr-FR')})
                      </span>
                    )}
                  </span>
                )}
                {snapshotStatus.status === 'error' && (
                  <span className="text-red-600"><X className="w-3 h-3 inline mr-1" />Erreur : {snapshotStatus.error}</span>
                )}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={triggerSnapshot} disabled={snapshotting || snapshotStatus?.status === 'running'}
              variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="snapshot-prix-btn">
              <Camera className={`w-4 h-4 mr-2 ${snapshotting ? 'animate-pulse' : ''}`} />
              Snapshot prix
            </Button>
            <Button onClick={fetchAllListings} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600" data-testid="refresh-listings-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Actualiser
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'bg-slate-100 text-slate-700', icon: Package },
            { label: 'Actives', value: stats.active, color: 'bg-emerald-50 text-emerald-700', icon: TrendingUp },
            { label: 'BackBox', value: stats.backbox, color: 'bg-blue-50 text-blue-700', icon: Box },
            { label: 'Hors BB', value: stats.notBackbox, color: 'bg-red-50 text-red-700', icon: X },
            { label: 'Rupture', value: stats.outOfStock, color: 'bg-amber-50 text-amber-700', icon: TrendingDown },
            { label: 'Prix cible', value: stats.withTarget, color: 'bg-purple-50 text-purple-700', icon: Target },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Card className={`border-0 ${s.color}`} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="py-3 px-3 flex items-center gap-2">
                  <s.icon className="w-4 h-4 opacity-60" />
                  <div><p className="text-xl font-bold">{s.value}</p><p className="text-xs opacity-60">{s.label}</p></div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-3 pb-3">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="SKU, titre, ID..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-9" data-testid="search-listings-input" />
              </div>
              <Select value={filterGrade} onValueChange={setFilterGrade}>
                <SelectTrigger className="w-[130px] h-9" data-testid="filter-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous grades</SelectItem>
                  <SelectItem value="SHINY">Comme neuf</SelectItem>
                  <SelectItem value="STALLONE">Tres bon</SelectItem>
                  <SelectItem value="GOOD">Bon etat</SelectItem>
                  <SelectItem value="FAIR">Correct</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStock} onValueChange={setFilterStock}>
                <SelectTrigger className="w-[120px] h-9" data-testid="filter-stock"><SelectValue placeholder="Stock" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout stock</SelectItem>
                  <SelectItem value="in">En stock</SelectItem>
                  <SelectItem value="out">Rupture</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterBackbox} onValueChange={setFilterBackbox}>
                <SelectTrigger className="w-[130px] h-9" data-testid="filter-backbox"><SelectValue placeholder="BackBox" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout BB</SelectItem>
                  <SelectItem value="yes">BackBox</SelectItem>
                  <SelectItem value="no">Non BackBox</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterPricing} onValueChange={setFilterPricing}>
                <SelectTrigger className="w-[150px] h-9" data-testid="filter-pricing"><SelectValue placeholder="Strategie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes strategies</SelectItem>
                  <SelectItem value="above_bb">Au dessus BB</SelectItem>
                  <SelectItem value="no_target">Sans prix cible</SelectItem>
                  <SelectItem value="below_min">Sous prix cible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {filteredListings.length} annonce(s)
              {filterPricing !== 'all' || filterBackbox !== 'all' || filterStock !== 'all' || filterGrade !== 'all' || searchQuery
                ? <span className="text-slate-400 font-normal ml-2">/ {stats.total}</span> : null}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                <span className="ml-3 text-slate-500">Chargement...</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left bg-slate-50/50">
                      <th className="py-2.5 px-3 font-medium text-slate-500 cursor-pointer select-none text-xs" onClick={() => toggleSort('sku')}>
                        SKU <SortIcon field="sku" />
                      </th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 cursor-pointer select-none text-xs" onClick={() => toggleSort('title')}>
                        Produit <SortIcon field="title" />
                      </th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 text-xs">Grade</th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 cursor-pointer select-none text-xs text-right" onClick={() => toggleSort('price')}>
                        Prix actuel <SortIcon field="price" />
                      </th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 text-xs text-right">Prix min</th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 text-xs text-right">Prix cible</th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 cursor-pointer select-none text-xs text-center" onClick={() => toggleSort('backbox_gap')}>
                        BackBox <SortIcon field="backbox_gap" />
                      </th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 cursor-pointer select-none text-xs text-center" onClick={() => toggleSort('quantity')}>
                        Stock <SortIcon field="quantity" />
                      </th>
                      <th className="py-2.5 px-3 font-medium text-slate-500 text-xs text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredListings.map((listing, idx) => {
                      const price = parseFloat(listing.price) || 0;
                      const minPrice = listing.min_price ? parseFloat(listing.min_price) : null;
                      const targetPrice = listing.target_price || null;
                      const hasStock = (listing.quantity || 0) > 0;
                      const isPublished = listing.publication_state === 2;
                      
                      return (
                        <motion.tr key={listing.listing_id || idx}
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          transition={{ delay: Math.min(idx * 0.005, 0.2) }}
                          className={`border-b border-slate-100 hover:bg-slate-50/80 transition-colors ${!hasStock ? 'opacity-50' : ''}`}
                          data-testid={`listing-row-${listing.listing_id}`}
                        >
                          <td className="py-2.5 px-3">
                            <span className="font-mono text-xs text-slate-600 font-medium">{listing.sku}</span>
                          </td>
                          <td className="py-2.5 px-3">
                            <p className="font-medium text-slate-800 line-clamp-1 max-w-[250px] text-xs">{listing.title}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {!isPublished && <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-300 text-amber-600">Hors ligne</Badge>}
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="text-xs text-slate-600">{GRADE_LABELS[listing.grade] || listing.grade}</span>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <span className="font-semibold text-slate-900 text-sm">{price.toFixed(0)} EUR</span>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            {minPrice ? (
                              <span className="text-xs text-slate-500">{minPrice.toFixed(0)} EUR</span>
                            ) : (
                              <span className="text-slate-300 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            {targetPrice ? (
                              <span className={`text-xs font-medium ${price >= targetPrice ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {targetPrice.toFixed(0)} EUR
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <BackBoxIndicator listing={listing} />
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <Badge className={hasStock ? 'bg-emerald-100 text-emerald-800 border-0 text-xs' : 'bg-red-100 text-red-800 border-0 text-xs'}>
                              {listing.quantity ?? 0}
                            </Badge>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <div className="flex items-center justify-center gap-0.5">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Historique de prix"
                                onClick={() => setHistoryListing(listing)}
                                data-testid={`history-listing-${listing.listing_id}`}>
                                <LineChartIcon className="w-3.5 h-3.5 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Gérer les prix"
                                onClick={() => openEdit(listing)}
                                data-testid={`edit-listing-${listing.listing_id}`}>
                                <Settings2 className="w-3.5 h-3.5 text-slate-500" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredListings.length === 0 && !loading && (
                  <div className="text-center py-12 text-slate-500">Aucune annonce trouvee</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit/Price Management Dialog */}
      <Dialog open={!!editingListing} onOpenChange={(open) => { if (!open) setEditingListing(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-listing-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-emerald-600 font-mono mr-2">{editingListing?.sku}</span>
              Gestion des prix
            </DialogTitle>
            <DialogDescription className="line-clamp-1">{editingListing?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* Current BackBox status */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <span className="text-sm text-slate-600">Statut BackBox</span>
              {editingListing?.snowden ? (
                <Badge className="bg-blue-100 text-blue-800 border-0"><Check className="w-3 h-3 mr-1" />BackBox</Badge>
              ) : (
                <Badge className="bg-slate-200 text-slate-600 border-0"><X className="w-3 h-3 mr-1" />Non BackBox</Badge>
              )}
            </div>

            {/* BM Price & Stock */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Prix BM (EUR)
                </Label>
                <Input type="number" step="0.01" value={editForm.price}
                  onChange={(e) => setEditForm(p => ({ ...p, price: e.target.value }))}
                  className="h-9" data-testid="edit-price-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <Package className="w-3 h-3" /> Stock
                </Label>
                <Input type="number" min="0" value={editForm.quantity}
                  onChange={(e) => setEditForm(p => ({ ...p, quantity: e.target.value }))}
                  className="h-9" data-testid="edit-quantity-input" />
              </div>
            </div>

            {/* Strategy prices */}
            <div className="border-t border-slate-200 pt-4">
              <p className="text-xs font-medium text-slate-500 mb-3 uppercase tracking-wide">Strategie tarifaire</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Prix minimum</Label>
                  <Input type="number" step="0.01" value={editForm.min_price} placeholder="Min"
                    onChange={(e) => setEditForm(p => ({ ...p, min_price: e.target.value }))}
                    className="h-9" data-testid="edit-min-price-input" />
                  <p className="text-[10px] text-slate-400">Mis a jour sur BM</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Prix cible</Label>
                  <Input type="number" step="0.01" value={editForm.target_price} placeholder="Cible"
                    onChange={(e) => setEditForm(p => ({ ...p, target_price: e.target.value }))}
                    className="h-9" data-testid="edit-target-price-input" />
                  <p className="text-[10px] text-slate-400">Stocke localement</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Prix BackBox</Label>
                  <Input type="number" step="0.01" value={editForm.backbox_price} placeholder="BB"
                    onChange={(e) => setEditForm(p => ({ ...p, backbox_price: e.target.value }))}
                    className="h-9" data-testid="edit-backbox-price-input" />
                  <p className="text-[10px] text-slate-400">Depuis BM back-office</p>
                </div>
              </div>
            </div>

            {/* Visual helper */}
            {editForm.backbox_price && editForm.price && (
              <div className={`p-3 rounded-lg text-sm ${parseFloat(editForm.price) <= parseFloat(editForm.backbox_price) ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {parseFloat(editForm.price) <= parseFloat(editForm.backbox_price) 
                  ? `Prix (${editForm.price} EUR) <= BackBox (${editForm.backbox_price} EUR) : vous etes eligible BackBox`
                  : `Prix (${editForm.price} EUR) > BackBox (${editForm.backbox_price} EUR) : baissez de ${(parseFloat(editForm.price) - parseFloat(editForm.backbox_price)).toFixed(0)} EUR pour obtenir la BackBox`
                }
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditingListing(null)} data-testid="cancel-edit-btn">Annuler</Button>
            <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={handleSave} disabled={updating}
              data-testid="save-listing-btn">
              {updating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price history modal */}
      {historyListing && (
        <ListingPriceHistoryModal
          listingId={historyListing.listing_id}
          listingLabel={`${historyListing.sku || ''} — ${historyListing.title || ''}`}
          onClose={() => setHistoryListing(null)}
        />
      )}
    </DashboardLayout>
  );
}
